import {
  forwardRef,
  type ReactNode,
  useImperativeHandle,
  useState,
} from "react";
import { Box, CodeBlockIcon, IconButton, Text, Tooltip } from "@hightouch/htui";

import { EmailAssetSelectionModal } from "./email-asset-selection-modal";
import type { SelectedComponent, SelectedEmailAsset } from "./types";

export type SelectComponentsButtonRef = {
  open: () => void;
};

/**
 * Component picking without a trigger: `open` shows the picker modal and
 * `element` is that modal, which the caller must mount somewhere stable —
 * e.g. outside a lazily-mounted menu — so the open modal isn't torn down with
 * its trigger.
 */
export const useComponentsPicker = ({
  selectedComponents,
  onChange,
}: {
  selectedComponents: SelectedComponent[];
  onChange: (components: SelectedComponent[]) => void;
}): { open: () => void; element: ReactNode } => {
  const [isOpen, setIsOpen] = useState(false);

  const element = isOpen ? (
    <EmailAssetSelectionModal
      isOpen={isOpen}
      sources={["component"]}
      onClose={() => setIsOpen(false)}
      initialSelected={selectedComponents.map((component) => ({
        ...component,
        kind: "component" as const,
      }))}
      onConfirm={(components: SelectedEmailAsset[]) => {
        onChange(components);
        setIsOpen(false);
      }}
    />
  ) : null;

  return { open: () => setIsOpen(true), element };
};

type Props = {
  selectedComponents: SelectedComponent[];
  onChange: (components: SelectedComponent[]) => void;
  isDisabled?: boolean;
};

/**
 * Chat toolbar button that opens the component picker. Shows a count badge when
 * components are attached. Exposes an imperative `open()` so other affordances
 * (e.g. a slash command) can trigger it.
 */
export const SelectComponentsButton = forwardRef<
  SelectComponentsButtonRef,
  Props
>(({ selectedComponents, onChange, isDisabled }, ref) => {
  const { open, element } = useComponentsPicker({
    selectedComponents,
    onChange,
  });

  useImperativeHandle(ref, () => ({ open }), [open]);

  return (
    <>
      <Tooltip message="Add components from library" placement="bottom">
        <Box position="relative">
          <IconButton
            aria-label="Add components from library"
            icon={CodeBlockIcon}
            type="button"
            variant="tertiary"
            shape="circle"
            isDisabled={isDisabled}
            onClick={open}
          />
          {selectedComponents.length > 0 && (
            <Box
              position="absolute"
              top={0}
              right={0}
              bg="primary.base"
              borderRadius="full"
              width="14px"
              height="14px"
              display="flex"
              alignItems="center"
              justifyContent="center"
              pointerEvents="none"
            >
              <Text size="sm" color="white" fontWeight="semibold">
                {selectedComponents.length}
              </Text>
            </Box>
          )}
        </Box>
      </Tooltip>
      {element}
    </>
  );
});

SelectComponentsButton.displayName = "SelectComponentsButton";
